// frontend-dash/src/pages/AiAgent.js
import DOMPurify from 'dompurify';
import React, { useState, useEffect, useContext, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import AuthContext from '../context/AuthContext';
import { useSettings } from '../context/SettingsContext';
import {
    SparklesIcon, SendIcon, CalendarIcon, BlogIcon,
    CheckCircleIcon, RefreshIcon, CloseIcon,
    EditIcon, AlertCircleIcon, LocationIcon,
    DeleteIcon, CheckboxIcon, SettingsIcon,
    AddUserIcon, PersonIcon, UploadIcon
} from '../components/Icons';
import { initSocket, getSocket, disconnectSocket } from '../utils/socket';
import BobRichElements, { BobMarkdown, BobGroundingSources } from '../components/BobRichElements';

const AiAgent = () => {
    const { user, API_URL, getAssetUrl } = useContext(AuthContext);
    const { organization } = useSettings();
    const navigate = useNavigate();
    const chatContainerRef = useRef(null);
    const addMenuRef = useRef(null);
    const fileInputRef = useRef(null);

    const location = organization?.location || 'deiner Stadt';
    const INITIAL_MESSAGE = {
        role: 'assistant',
        content: {
            text: `Hallo! Ich bin Bob, dein KI-Assistent. Ich kann Events für dich erstellen, Blogbeiträge entwerfen, Aufgaben für Teammitglieder anlegen, Fragen beantworten und im Web recherchieren.\n\nDu kannst mich z. B. bitten:\n- *"Erstelle ein Zeltlager für den 10. Juli in ${location}"*\n- *"Recherchiere Pfadfinder-Knoten oder Jurten-Aufbau im Internet"*\n- *"Schreibe einen Elternbrief zum Sommerlager"*`,
            ui: [
                {
                    type: "quick_replies",
                    options: [
                        "Welche offenen Kontaktanfragen gibt es?",
                        "Recherchiere Pfadfinder-Knoten im Web",
                        "Zeige mir die kommenden Events",
                        "Erstelle eine neue Gruppenstunde"
                    ]
                }
            ],
            artifacts: { events: [], blogPosts: [], tasks: [], texts: [] }
        }
    };

    // Chat states
    const [messages, setMessages] = useState([INITIAL_MESSAGE]);
    const [chatList, setChatList] = useState([]);
    const [currentChatId, setCurrentChatId] = useState(null);
    const [currentParticipants, setCurrentParticipants] = useState([]);
    const [showAddMenu, setShowAddMenu] = useState(false);
    const [typingUsers, setTypingUsers] = useState({});
    const [input, setInput] = useState('');
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');
    const [uploadingImage, setUploadingImage] = useState(false);
    const [uploadedImage, setUploadedImage] = useState(null); // { url, key }

    // Proposed Artifacts
    const [proposedArtifacts, setProposedArtifacts] = useState({
        events: [],
        blogPosts: [],
        tasks: [],
        texts: [],
        knowledge: []
    });

    // Proposed Actions (settings updates etc.)
    const [proposedActions, setProposedActions] = useState([]);

    // Active tab in artifacts panel: 'events' | 'blogs' | 'tasks' | 'actions'
    const [activeTab, setActiveTab] = useState('events');

    // Modals for editing artifacts
    const [editModal, setEditModal] = useState({ type: '', index: null, data: null });

    // Save states
    const [saving, setSaving] = useState(false);
    const [saveProgress, setSaveProgress] = useState('');
    const [saveResult, setSaveResult] = useState(null); // { eventIds: [], blogPostIds: [], taskIds: [] }
    const [saveSuccess, setSaveSuccess] = useState(false);

    // Team members & existing events (fetched for dropdowns / helper in editing modals)
    const [teamMembers, setTeamMembers] = useState([]);
    const [existingEvents, setExistingEvents] = useState([]);
    const [copiedTextId, setCopiedTextId] = useState(null);

    const config = { headers: { Authorization: `Bearer ${user.token}` } };

    // Scroll to bottom of chat
    useEffect(() => {
        if (messages.length > 1 || loading) {
            if (chatContainerRef.current) {
                chatContainerRef.current.scrollTo({
                    top: chatContainerRef.current.scrollHeight,
                    behavior: 'smooth'
                });
            }
        }
    }, [messages, loading]);

    // Close add member menu when clicking outside
    useEffect(() => {
        const handleClickOutside = (event) => {
            if (addMenuRef.current && !addMenuRef.current.contains(event.target)) {
                setShowAddMenu(false);
            }
        };
        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, []);

    const currentChatIdRef = useRef(currentChatId);
    useEffect(() => {
        currentChatIdRef.current = currentChatId;
    }, [currentChatId]);

    // Fetch context helper data on mount & Setup Socket connection
    useEffect(() => {
        const fetchContextData = async () => {
            try {
                // Fetch team members
                const userRes = await axios.get(`${API_URL}/users`, config);
                setTeamMembers(userRes.data.filter(u => u.role === 'gruppenleiter' || u.role === 'admin' || u.role === 'kind'));

                // Fetch existing events
                const eventRes = await axios.get(`${API_URL}/events`, config);
                setExistingEvents(eventRes.data);
            } catch (err) {
                console.error('Error fetching AI context helpers:', err);
            }
        };
        fetchContextData();
        fetchChatList();

        // Initialize socket
        const socket = initSocket(user.token, API_URL);

        if (socket) {
            socket.on('receive_message', (data) => {
                if (data && data.chatId === currentChatIdRef.current) {
                    setMessages(data.messages);
                }
                fetchChatList();
            });

            socket.on('user_typing', ({ chatId, userName, isTyping }) => {
                if (chatId === currentChatIdRef.current) {
                    setTypingUsers(prev => ({
                        ...prev,
                        [userName]: isTyping
                    }));
                }
            });

            socket.on('chat_members_updated', ({ chatId }) => {
                if (chatId === currentChatIdRef.current) {
                    // Refresh chat details to load updated participant list
                    handleSelectChat(chatId);
                }
            });

            socket.on('added_to_chat', () => {
                fetchChatList();
            });

            socket.on('removed_from_chat', ({ chatId }) => {
                fetchChatList();
                if (currentChatIdRef.current === chatId) {
                    handleNewChat();
                }
            });
        }

        return () => {
            if (socket) {
                socket.off('receive_message');
                socket.off('user_typing');
                socket.off('chat_members_updated');
                socket.off('added_to_chat');
                socket.off('removed_from_chat');
                disconnectSocket();
            }
        };
        // eslint-disable-next-line
    }, []);

    // Handle room joining / leaving
    useEffect(() => {
        const socket = getSocket();
        if (socket && currentChatId) {
            socket.emit('join_chat', currentChatId);
        }
        return () => {
            const socket = getSocket();
            if (socket && currentChatId) {
                socket.emit('leave_chat', currentChatId);
            }
        };
    }, [currentChatId]);

    const fetchChatList = async () => {
        try {
            const { data } = await axios.get(`${API_URL}/ai/agent/chats`, config);
            setChatList(data);
        } catch (err) {
            console.error('Error fetching chat list:', err);
        }
    };

    const handleSelectChat = async (id) => {
        try {
            const { data } = await axios.get(`${API_URL}/ai/agent/chats/${id}`, config);
            setCurrentChatId(data._id);
            setCurrentParticipants(data.participants || []);
            setTypingUsers({});
            if (data.messages && data.messages.length > 0) {
                setMessages(data.messages);
            } else {
                setMessages([INITIAL_MESSAGE]);
            }
            setProposedArtifacts({ events: [], blogPosts: [], tasks: [], texts: [] });
            setProposedActions([]);
        } catch (err) {
            console.error('Error fetching chat:', err);
            setError('Fehler beim Laden des Chats.');
        }
    };

    const handleNewChat = () => {
        setCurrentChatId(null);
        setCurrentParticipants([]);
        setTypingUsers({});
        setMessages([INITIAL_MESSAGE]);
        setProposedArtifacts({ events: [], blogPosts: [], tasks: [], texts: [] });
        setProposedActions([]);
    };

    const handleDeleteChat = async (id, e) => {
        e.stopPropagation();
        if (!window.confirm('Diesen Chat wirklich löschen?')) return;
        try {
            await axios.delete(`${API_URL}/ai/agent/chats/${id}`, config);
            if (currentChatId === id) {
                handleNewChat();
            }
            fetchChatList();
        } catch (err) {
            console.error('Error deleting chat:', err);
            setError('Fehler beim Löschen des Chats.');
        }
    };

    const handleAddParticipant = async (userId) => {
        if (!currentChatId) return;
        const updated = [...currentParticipants, userId];
        try {
            await axios.put(`${API_URL}/ai/agent/chats/${currentChatId}/participants`, { participants: updated }, config);
            setCurrentParticipants(updated);

            const socket = getSocket();
            if (socket) {
                socket.emit('add_to_chat', { chatId: currentChatId, userId });
            }
        } catch (err) {
            console.error('Error adding participant:', err);
            setError('Teilnehmer konnte nicht hinzugefügt werden.');
        }
    };

    const handleRemoveParticipant = async (userId) => {
        if (!currentChatId) return;
        const updated = currentParticipants.filter(id => id !== userId);
        try {
            await axios.put(`${API_URL}/ai/agent/chats/${currentChatId}/participants`, { participants: updated }, config);
            setCurrentParticipants(updated);

            const socket = getSocket();
            if (socket) {
                socket.emit('remove_from_chat', { chatId: currentChatId, userId });
            }
        } catch (err) {
            console.error('Error removing participant:', err);
            setError('Teilnehmer konnte nicht entfernt werden.');
        }
    };

    // Tab helper counts
    const artifactCounts = {
        events: proposedArtifacts.events.length,
        blogs: proposedArtifacts.blogPosts.length,
        tasks: proposedArtifacts.tasks.length,
        texts: proposedArtifacts.texts.length,
        knowledge: (proposedArtifacts.knowledge || []).length,
        actions: proposedActions.length
    };

    // Auto-switch active tab when new artifacts arrive
    useEffect(() => {
        if (proposedArtifacts.events.length > 0) setActiveTab('events');
        else if (proposedArtifacts.blogPosts.length > 0) setActiveTab('blogs');
        else if (proposedArtifacts.tasks.length > 0) setActiveTab('tasks');
        else if (proposedArtifacts.texts.length > 0) setActiveTab('texts');
        else if (proposedActions.length > 0) setActiveTab('actions');
    }, [proposedArtifacts, proposedActions]);

    const typingTimeoutRef = useRef(null);
    const handleInputChange = (e) => {
        const value = e.target.value;
        setInput(value);

        const socket = getSocket();
        if (socket && currentChatId) {
            socket.emit('typing', { chatId: currentChatId, isTyping: value.trim().length > 0 });

            if (typingTimeoutRef.current) clearTimeout(typingTimeoutRef.current);
            typingTimeoutRef.current = setTimeout(() => {
                socket.emit('typing', { chatId: currentChatId, isTyping: false });
            }, 3000);
        }
    };

    const handleImageUpload = async (e) => {
        const file = e.target.files[0];
        if (!file) return;

        setUploadingImage(true);
        setError('');

        const formData = new FormData();
        formData.append('file', file);

        try {
            const res = await axios.post(`${API_URL}/files/upload`, formData, config);
            setUploadedImage({
                url: res.data.url,
                key: res.data.key
            });
        } catch (err) {
            console.error('Error uploading image to Bob chat:', err);
            setError('Bild-Upload fehlgeschlagen.');
        } finally {
            setUploadingImage(false);
            if (fileInputRef.current) {
                fileInputRef.current.value = '';
            }
        }
    };

    const handlePaste = async (e) => {
        const items = e.clipboardData?.items;
        if (!items) return;
        for (let i = 0; i < items.length; i++) {
            if (items[i].type.indexOf('image') !== -1) {
                const file = items[i].getAsFile();
                if (file) {
                    e.preventDefault();
                    setUploadingImage(true);
                    setError('');
                    const formData = new FormData();
                    formData.append('file', file);
                    try {
                        const res = await axios.post(`${API_URL}/files/upload`, formData, config);
                        setUploadedImage({
                            url: res.data.url,
                            key: res.data.key
                        });
                    } catch (err) {
                        console.error('Error uploading pasted image to Bob chat:', err);
                        setError('Bild-Upload fehlgeschlagen.');
                    } finally {
                        setUploadingImage(false);
                    }
                    break;
                }
            }
        }
    };

    const sendMessageWithPrompt = async (promptText) => {
        if ((!promptText?.trim() && !uploadedImage) || loading) return;

        const userPrompt = (promptText || '').trim();
        const finalPrompt = uploadedImage
            ? `${userPrompt}\n\n[Angehängtes Bild: ${uploadedImage.key}]`.trim()
            : userPrompt;

        setInput('');
        setError('');

        const socket = getSocket();
        if (socket && currentChatId) {
            socket.emit('typing', { chatId: currentChatId, isTyping: false });
            if (typingTimeoutRef.current) clearTimeout(typingTimeoutRef.current);
        }

        // Store and reset uploaded image
        const currentUploadedImage = uploadedImage;
        setUploadedImage(null);

        // Add user message
        const newMessages = [...messages, {
            role: 'user',
            content: finalPrompt,
            imageUrl: currentUploadedImage?.url,
            imageKey: currentUploadedImage?.key
        }];
        setMessages(newMessages);
        setLoading(true);

        try {
            const controller = new AbortController();
            const timeout = setTimeout(() => controller.abort(), 60000); // 60s timeout

            const payload = {
                messages: newMessages.slice(0, -1),
                prompt: finalPrompt
            };
            if (currentUploadedImage) {
                payload.imageUrl = currentUploadedImage.url;
                payload.imageKey = currentUploadedImage.key;
            }
            if (currentChatId) {
                payload.chatId = currentChatId;
            }

            // Post chat to endpoint
            const { data } = await axios.post(`${API_URL}/ai/agent/chat`, payload, {
                ...config,
                signal: controller.signal
            });

            clearTimeout(timeout);

            if (data.chatId && !currentChatId) {
                setCurrentChatId(data.chatId);
                fetchChatList();
            }

            // Add agent message
            const finalMessages = [...newMessages, {
                role: 'assistant',
                content: data
            }];
            setMessages(finalMessages);

            const socket = getSocket();
            if (socket) {
                socket.emit('send_message', {
                    chatId: data.chatId || currentChatId,
                    messages: finalMessages
                });
            }

            // Merge proposed artifacts if any returned
            if (data.artifacts) {
                setProposedArtifacts(prev => {
                    const newEvents = [...prev.events];
                    const newBlogs = [...prev.blogPosts];
                    const newTasks = [...prev.tasks];
                    const newTexts = [...prev.texts];
                    const newKnowledge = [...(prev.knowledge || [])];

                    if (data.artifacts.events) {
                        data.artifacts.events.forEach(e => {
                            if (!e.tempId) e.tempId = `temp-event-${Date.now()}-${Math.random().toString(36).substr(2, 5)}`;
                            newEvents.push(e);
                        });
                    }
                    if (data.artifacts.blogPosts) {
                        data.artifacts.blogPosts.forEach(b => {
                            if (!b.tempId) b.tempId = `temp-blog-${Date.now()}-${Math.random().toString(36).substr(2, 5)}`;
                            newBlogs.push(b);
                        });
                    }
                    if (data.artifacts.tasks) {
                        data.artifacts.tasks.forEach(t => {
                            if (!t.tempId) t.tempId = `temp-task-${Date.now()}-${Math.random().toString(36).substr(2, 5)}`;
                            newTasks.push(t);
                        });
                    }
                    if (data.artifacts.texts) {
                        data.artifacts.texts.forEach(txt => {
                            if (!txt.tempId) txt.tempId = `temp-text-${Date.now()}-${Math.random().toString(36).substr(2, 5)}`;
                            newTexts.push(txt);
                        });
                    }
                    if (data.artifacts.knowledge) {
                        data.artifacts.knowledge.forEach(k => {
                            if (!k.tempId) k.tempId = `temp-know-${Date.now()}-${Math.random().toString(36).substr(2, 5)}`;
                            newKnowledge.push(k);
                        });
                    }

                    return { events: newEvents, blogPosts: newBlogs, tasks: newTasks, texts: newTexts, knowledge: newKnowledge };
                });
            }

            // Merge proposed actions if any returned
            if (data.actions && Array.isArray(data.actions) && data.actions.length > 0) {
                setProposedActions(prev => [...prev, ...data.actions]);
            }
        } catch (err) {
            console.error('AI Agent chat request failed:', err);
            let errMsg = 'Fehler bei der Anfrage.';
            if (err.name === 'CanceledError' || err.code === 'ECONNABORTED') {
                errMsg = 'Die KI-Antwort hat zu lange gedauert (Timeout). Bitte versuchen Sie es erneut.';
            } else if (err.response?.data?.message) {
                errMsg = err.response.data.message;
            }
            setError(errMsg);
            setMessages(prev => [...prev, {
                role: 'assistant',
                content: { text: `⚠️ Fehler: ${errMsg}`, artifacts: null }
            }]);
        } finally {
            setLoading(false);
        }
    };

    const handleSend = (e) => {
        if (e) e.preventDefault();
        sendMessageWithPrompt(input);
    };

    const handleQuickReply = (optionText) => {
        sendMessageWithPrompt(optionText);
    };

    const handleRichAction = (action) => {
        if (action?.type === 'navigate' && action.url) {
            if (action.url.startsWith('http')) {
                window.open(action.url, '_blank', 'noopener,noreferrer');
            } else {
                navigate(action.url);
            }
        }
    };

    // Remove proposed artifact
    const handleRemoveArtifact = (type, index) => {
        setProposedArtifacts(prev => {
            const list = [...prev[type]];
            list.splice(index, 1);
            return { ...prev, [type]: list };
        });
    };

    // Remove proposed action
    const handleRemoveAction = (index) => {
        setProposedActions(prev => {
            const list = [...prev];
            list.splice(index, 1);
            return list;
        });
    };

    // Open edit modal for proposed artifact
    const handleOpenEdit = (type, index) => {
        const item = proposedArtifacts[type][index];
        setEditModal({ type, index, data: { ...item } });
    };

    // Open edit modal for proposed action
    const handleOpenActionEdit = (index) => {
        const action = proposedActions[index];
        setEditModal({ type: 'action', index, data: { ...action, data: { ...action.data } } });
    };

    // Save edited artifact/action from modal
    const handleSaveEdit = (e) => {
        e.preventDefault();
        const { type, index, data } = editModal;
        if (type === 'action') {
            setProposedActions(prev => {
                const list = [...prev];
                list[index] = data;
                return list;
            });
        } else {
            setProposedArtifacts(prev => {
                const list = [...prev[type]];
                list[index] = data;
                return { ...prev, [type]: list };
            });
        }
        setEditModal({ type: '', index: null, data: null });
    };

    // Create all proposed artifacts sequentially
    const handleSaveAll = async () => {
        setSaving(true);
        setSaveSuccess(false);
        setError('');

        const tempIdMap = {};
        const createdIds = { events: [], blogPosts: [], tasks: [] };

        try {
            // 1. Create Events
            if (proposedArtifacts.events.length > 0) {
                setSaveProgress('Erstelle Events...');
                for (let i = 0; i < proposedArtifacts.events.length; i++) {
                    const event = proposedArtifacts.events[i];
                    setSaveProgress(`Erstelle Event: "${event.title}" (${i + 1}/${proposedArtifacts.events.length})...`);

                    const eventPayload = { ...event };
                    delete eventPayload.tempId;
                    delete eventPayload._id;

                    const response = await axios.post(`${API_URL}/events`, eventPayload, config);
                    const actualId = response.data._id || response.data.eventId;
                    if (event.tempId) {
                        tempIdMap[event.tempId] = actualId;
                    }
                    createdIds.events.push(actualId);
                }
            }

            // 2. Create Blogposts (with replaced linkedEvent)
            if (proposedArtifacts.blogPosts.length > 0) {
                setSaveProgress('Erstelle Blogbeiträge...');
                for (let i = 0; i < proposedArtifacts.blogPosts.length; i++) {
                    const blog = { ...proposedArtifacts.blogPosts[i] };
                    setSaveProgress(`Erstelle Blogbeitrag: "${blog.title}" (${i + 1}/${proposedArtifacts.blogPosts.length})...`);

                    // Replace tempId with actualId
                    if (blog.linkedEvent && tempIdMap[blog.linkedEvent]) {
                        blog.linkedEvent = tempIdMap[blog.linkedEvent];
                    }

                    const blogPayload = { ...blog };
                    delete blogPayload.tempId;
                    delete blogPayload._id;

                    const response = await axios.post(`${API_URL}/blog`, blogPayload, config);
                    const actualId = response.data._id || response.data.blogPostId;
                    createdIds.blogPosts.push(actualId);
                }
            }

            // 3. Create Tasks (with replaced event)
            if (proposedArtifacts.tasks.length > 0) {
                setSaveProgress('Erstelle Aufgaben...');
                for (let i = 0; i < proposedArtifacts.tasks.length; i++) {
                    const task = { ...proposedArtifacts.tasks[i] };
                    setSaveProgress(`Erstelle Aufgabe: "${task.title}" (${i + 1}/${proposedArtifacts.tasks.length})...`);

                    // Replace tempId with actualId
                    if (task.event && tempIdMap[task.event]) {
                        task.event = tempIdMap[task.event];
                    }

                    const taskPayload = { ...task };
                    delete taskPayload.tempId;
                    delete taskPayload._id;

                    const response = await axios.post(`${API_URL}/tasks`, taskPayload, config);
                    const actualId = response.data._id || response.data.taskId;
                    createdIds.tasks.push(actualId);
                }
            }

            // 4. Execute Actions (settings updates etc.)
            if (proposedActions.length > 0) {
                setSaveProgress('Führe Aktionen aus...');
                for (let i = 0; i < proposedActions.length; i++) {
                    const action = proposedActions[i];
                    setSaveProgress(`Aktion: "${action.label || action.type}" (${i + 1}/${proposedActions.length})...`);

                    if (action.type === 'update_settings' && action.key) {
                        await axios.put(`${API_URL}/settings/${action.key}`, action.data, config);
                    } else if (action.type === 'update_profile' && action.data) {
                        const updateBody = {};
                        const rootFields = ['enableEmailRouting', 'officialEmail'];
                        Object.entries(action.data).forEach(([k, v]) => {
                            const isRoot = rootFields.includes(k) || k.startsWith('notificationSettings');
                            if (isRoot || k.startsWith('userData.')) {
                                updateBody[k] = v;
                            } else {
                                updateBody[`userData.${k}`] = v;
                            }
                        });
                        await axios.put(`${API_URL}/users/profile`, updateBody, config);
                    } else if (action.type === 'update_team_member' && action.userId) {
                        const updateBody = {};
                        const rootFields = ['enableEmailRouting', 'officialEmail'];
                        Object.entries(action.data || {}).forEach(([k, v]) => {
                            const isRoot = rootFields.includes(k) || k.startsWith('notificationSettings');
                            if (isRoot || k.startsWith('userData.')) {
                                updateBody[k] = v;
                            } else {
                                updateBody[`userData.${k}`] = v;
                            }
                        });
                        await axios.put(`${API_URL}/users/${action.userId}/profile`, updateBody, config);
                    } else if (action.type === 'setup_email_routing' && action.userId) {
                        await axios.post(`${API_URL}/cloudflare/setup-user/${action.userId}`, {}, config);
                    } else if (action.type === 'notify_devino' && action.message) {
                        // Silent notification - fire and forget
                        axios.post(`${API_URL}/ai/notify-devino`, { message: action.message }, config).catch(() => { });
                    } else if (action.type === 'update_attendance') {
                        await axios.post(`${API_URL}/attendance`, {
                            group: action.groupId,
                            date: action.date,
                            title: action.title || 'Gruppenstunde',
                            type: action.attendanceType || 'gruppenstunde',
                            records: (action.records || []).map(r => ({ user: r.user, status: r.status }))
                        }, config);
                    } else if (action.type === 'add_group_member') {
                        await axios.post(`${API_URL}/groups/${action.groupId}/members`, { userIds: [action.userId] }, config);
                    } else if (action.type === 'remove_group_member') {
                        await axios.delete(`${API_URL}/groups/${action.groupId}/members/${action.userId}`, config);
                    } else if (action.type === 'approve_event_registration') {
                        await axios.put(`${API_URL}/events/registrations/${action.registrationId}/approve`, {}, config);
                    } else if (action.type === 'approve_user') {
                        await axios.put(`${API_URL}/users/${action.userId}/approve`, { isApproved: true }, config);
                    } else if (action.type === 'send_mailbox_email') {
                        await axios.post(`${API_URL}/emails/send`, {
                            to: action.recipient || action.to,
                            recipient: action.recipient || action.to,
                            subject: action.subject,
                            message: action.body || action.message,
                            body: action.body || action.message,
                            html: action.html
                        }, config);
                    } else if (action.type === 'update_contact_request') {
                        await axios.put(`${API_URL}/contact/${action.contactId}`, { isResolved: action.isResolved }, config);
                    } else if (action.type === 'create_finance_transaction') {
                        await axios.post(`${API_URL}/finances/transactions`, action.data || action, config);
                    } else if (action.type === 'create_inventory_item') {
                        await axios.post(`${API_URL}/inventory/items`, action.data || action, config);
                    } else if (action.type === 'update_inventory_item' && action.itemId) {
                        await axios.put(`${API_URL}/inventory/items/${action.itemId}`, action.data || action, config);
                    } else if (action.type === 'create_inventory_lending') {
                        await axios.post(`${API_URL}/inventory/lendings`, action.data || action, config);
                    } else if (action.type === 'return_inventory_lending' && action.lendingId) {
                        await axios.post(`${API_URL}/inventory/lendings/${action.lendingId}/return`, action.data || action, config);
                    } else if (action.type === 'add_knowledge') {
                        await axios.post(`${API_URL}/ai/knowledge/add`, action.data || action, config);
                    }
                }
            }

            // 5. Save Knowledge Base Items
            if (proposedArtifacts.knowledge && proposedArtifacts.knowledge.length > 0) {
                setSaveProgress('Speichere Wissensdatenbank-Einträge...');
                for (let i = 0; i < proposedArtifacts.knowledge.length; i++) {
                    const item = proposedArtifacts.knowledge[i];
                    setSaveProgress(`Wissenseintrag: "${item.title}" (${i + 1}/${proposedArtifacts.knowledge.length})...`);
                    const response = await axios.post(`${API_URL}/ai/knowledge/add`, item, config);
                    createdIds.knowledge = createdIds.knowledge || [];
                    createdIds.knowledge.push(response.data?.entry?.id || item.title);
                }
            }

            // Include texts acknowledged
            createdIds.texts = (proposedArtifacts.texts || []).map(t => t.tempId || t.title);

            // Clear proposed list and show success
            setProposedArtifacts({ events: [], blogPosts: [], tasks: [], texts: [], knowledge: [] });
            setProposedActions([]);
            setSaveResult(createdIds);
            setSaveSuccess(true);
        } catch (err) {
            console.error('Error saving proposed artifacts:', err);
            setError(err.response?.data?.message || 'Fehler beim Speichern der Artefakte.');
        } finally {
            setSaving(false);
            setSaveProgress('');
        }
    };

    // Get name of team member by ID
    const getMemberName = (id) => {
        const m = teamMembers.find(u => u._id === id);
        return m ? (m.userData?.displayName || m.userData?.firstName || 'Nutzer') : 'Unbekannt';
    };

    // Get title of event (proposed or existing) by ID/tempId
    const getEventTitle = (id) => {
        // Check proposed first
        const prop = proposedArtifacts.events.find(e => e.tempId === id || e._id === id);
        if (prop) return `[Vorgeschlagen] ${prop.title}`;

        // Check existing
        const ext = existingEvents.find(e => e._id === id);
        return ext ? ext.title : 'Unbekanntes Event';
    };

    const renderText = (text) => {
        if (!text) return '';
        let html = text.replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>');
        html = html.replace(/\*(.*?)\*/g, '<em>$1</em>');
        return html;
    };


    const renderInlineArtifacts = (artifacts, actions) => {
        if (!artifacts && (!actions || actions.length === 0)) return null;
        return (
            <div className="w-full flex flex-col gap-3 mt-3">
                {artifacts?.events?.map((e, eIdx) => {
                    const gIdx = proposedArtifacts.events.findIndex(x => x.tempId === e.tempId);
                    const item = gIdx >= 0 ? proposedArtifacts.events[gIdx] : e;
                    if (!item) return null;
                    const isProposed = gIdx >= 0;
                    return (
                        <div key={'e-' + eIdx} className="md3-card-outlined p-4 relative group hover:shadow-md transition-shadow animate-scale-in bg-surface-containerLow border-l-4 border-l-primary">
                            <div className="flex items-center justify-between gap-2 pr-16">
                                <h4 className="font-bold text-body-large text-surface-on">{item.title}</h4>
                                {!isProposed && (
                                    <span className="text-[10px] font-bold px-2 py-0.5 rounded bg-surface-containerHigh text-surface-onVariant flex-shrink-0">
                                        Bestätigt / Verlauf
                                    </span>
                                )}
                            </div>
                            <p className="text-body-small text-surface-onVariant/80 line-clamp-2 mt-1">{item.description}</p>
                            <div className="flex flex-wrap gap-x-4 gap-y-1 mt-3 text-label-small text-surface-onVariant">
                                <span className="flex items-center gap-1"><CalendarIcon style={{ fontSize: 12 }} /> {item.date || 'TBD'}</span>
                                {item.location && <span className="flex items-center gap-1"><LocationIcon style={{ fontSize: 12 }} /> {item.location}</span>}
                            </div>
                            {isProposed && (
                                <div className="absolute top-2 right-2 flex gap-1 opacity-60 group-hover:opacity-100 transition-opacity">
                                    <button onClick={() => handleOpenEdit('events', gIdx)} className="md3-icon-btn hover:bg-surface-containerHigh text-surface-on" title="Bearbeiten">
                                        <EditIcon style={{ fontSize: 16 }} />
                                    </button>
                                    <button onClick={() => handleRemoveArtifact('events', gIdx)} className="md3-icon-btn hover:bg-error-container text-error" title="Entfernen">
                                        <DeleteIcon style={{ fontSize: 16 }} />
                                    </button>
                                </div>
                            )}
                        </div>
                    );
                })}
                {artifacts?.blogPosts?.map((b, bIdx) => {
                    const gIdx = proposedArtifacts.blogPosts.findIndex(x => x.tempId === b.tempId);
                    const item = gIdx >= 0 ? proposedArtifacts.blogPosts[gIdx] : b;
                    if (!item) return null;
                    const isProposed = gIdx >= 0;
                    return (
                        <div key={'b-' + bIdx} className="md3-card-outlined p-4 relative group hover:shadow-md transition-shadow animate-scale-in bg-surface-containerLow border-l-4 border-l-primary">
                            <div className="flex items-center justify-between gap-2 pr-16">
                                <h4 className="font-bold text-body-large text-surface-on">{item.title}</h4>
                                {!isProposed && (
                                    <span className="text-[10px] font-bold px-2 py-0.5 rounded bg-surface-containerHigh text-surface-onVariant flex-shrink-0">
                                        Bestätigt / Verlauf
                                    </span>
                                )}
                            </div>
                            {item.linkedEvent && (
                                <p className="text-[10px] font-black text-primary uppercase tracking-wider mt-1">
                                    🔗 Verknüpft: {getEventTitle(item.linkedEvent)}
                                </p>
                            )}
                            <div className="mt-2 text-body-small text-surface-onVariant prose prose-sm max-h-[80px] overflow-hidden opacity-80"
                                dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(item.content) }} />
                            {isProposed && (
                                <div className="absolute top-2 right-2 flex gap-1 opacity-60 group-hover:opacity-100 transition-opacity">
                                    <button onClick={() => handleOpenEdit('blogPosts', gIdx)} className="md3-icon-btn hover:bg-surface-containerHigh text-surface-on" title="Bearbeiten">
                                        <EditIcon style={{ fontSize: 16 }} />
                                    </button>
                                    <button onClick={() => handleRemoveArtifact('blogPosts', gIdx)} className="md3-icon-btn hover:bg-error-container text-error" title="Entfernen">
                                        <DeleteIcon style={{ fontSize: 16 }} />
                                    </button>
                                </div>
                            )}
                        </div>
                    );
                })}
                {artifacts?.tasks?.map((t, tIdx) => {
                    const gIdx = proposedArtifacts.tasks.findIndex(x => x.tempId === t.tempId);
                    const item = gIdx >= 0 ? proposedArtifacts.tasks[gIdx] : t;
                    if (!item) return null;
                    const isProposed = gIdx >= 0;
                    return (
                        <div key={'t-' + tIdx} className="md3-card-outlined p-4 relative group hover:shadow-md transition-shadow animate-scale-in bg-surface-containerLow border-l-4 border-l-primary">
                            <div className="flex items-center justify-between gap-2 pr-16">
                                <h4 className="font-bold text-body-large text-surface-on">{item.title}</h4>
                                {!isProposed && (
                                    <span className="text-[10px] font-bold px-2 py-0.5 rounded bg-surface-containerHigh text-surface-onVariant flex-shrink-0">
                                        Bestätigt / Verlauf
                                    </span>
                                )}
                            </div>
                            <p className="text-body-small text-surface-onVariant/80 mt-1">{item.description}</p>
                            {item.event && (
                                <p className="text-[10px] font-black text-primary uppercase tracking-wider mt-1">
                                    🔗 Event: {getEventTitle(item.event)}
                                </p>
                            )}
                            <div className="flex flex-wrap gap-2 mt-3 text-label-small">
                                <span className={`px-2 py-0.5 rounded font-bold uppercase tracking-wider ${item.priority === 'urgent' || item.priority === 'high' ? 'bg-error-container text-error-on-container' : 'bg-surface-containerHigh text-surface-onVariant'
                                    }`}>
                                    Prio: {item.priority}
                                </span>
                                {item.assignees?.map((aId, aIdx) => (
                                    <span key={aIdx} className="bg-secondary-container text-secondary-onContainer px-2 py-0.5 rounded font-medium">
                                        👤 {getMemberName(aId)}
                                    </span>
                                ))}
                            </div>
                            {isProposed && (
                                <div className="absolute top-2 right-2 flex gap-1 opacity-60 group-hover:opacity-100 transition-opacity">
                                    <button onClick={() => handleOpenEdit('tasks', gIdx)} className="md3-icon-btn hover:bg-surface-containerHigh text-surface-on" title="Bearbeiten">
                                        <EditIcon style={{ fontSize: 16 }} />
                                    </button>
                                    <button onClick={() => handleRemoveArtifact('tasks', gIdx)} className="md3-icon-btn hover:bg-error-container text-error" title="Entfernen">
                                        <DeleteIcon style={{ fontSize: 16 }} />
                                    </button>
                                </div>
                            )}
                        </div>
                    );
                })}
                {artifacts?.texts?.map((txt, txtIdx) => {
                    const gIdx = proposedArtifacts.texts.findIndex(x => x.tempId === txt.tempId);
                    const item = gIdx >= 0 ? proposedArtifacts.texts[gIdx] : txt;
                    if (!item) return null;
                    const isProposed = gIdx >= 0;
                    const textIdentifier = item.tempId || 'text-' + txtIdx;
                    const isCopied = copiedTextId === textIdentifier;
                    return (
                        <div key={'txt-' + txtIdx} className="md3-card-outlined p-4 relative group hover:shadow-md transition-shadow animate-scale-in bg-surface-containerLow border-l-4 border-l-secondary">
                            <div className="flex items-center justify-between gap-2 pr-28">
                                <h4 className="font-bold text-body-large text-surface-on">{item.title}</h4>
                                {!isProposed && (
                                    <span className="text-[10px] font-bold px-2 py-0.5 rounded bg-surface-containerHigh text-surface-onVariant flex-shrink-0">
                                        Bestätigt / Verlauf
                                    </span>
                                )}
                            </div>
                            <div className="mt-2 text-body-small text-surface-onVariant prose prose-sm max-h-[150px] overflow-y-auto whitespace-pre-wrap opacity-80 border border-outlineVariant/20 p-2 rounded bg-surface">
                                {item.content}
                            </div>
                            <div className="absolute top-2 right-2 flex items-center gap-1">
                                <button
                                    onClick={() => {
                                        navigator.clipboard.writeText(item.content || '');
                                        setCopiedTextId(textIdentifier);
                                        setTimeout(() => setCopiedTextId(null), 2000);
                                    }}
                                    className={`px-2 py-1 rounded-lg text-xs font-bold transition-all flex items-center gap-1 ${isCopied
                                            ? 'bg-primary text-primary-on'
                                            : 'bg-surface-containerHigh hover:bg-surface-containerHighest text-surface-on border border-outlineVariant/30'
                                        }`}
                                    title="In Zwischenablage kopieren"
                                >
                                    {isCopied ? '✓ Kopiert' : '📋 Kopieren'}
                                </button>
                                {isProposed && (
                                    <>
                                        <button onClick={() => handleOpenEdit('texts', gIdx)} className="md3-icon-btn hover:bg-surface-containerHigh text-surface-on" title="Bearbeiten">
                                            <EditIcon style={{ fontSize: 16 }} />
                                        </button>
                                        <button onClick={() => handleRemoveArtifact('texts', gIdx)} className="md3-icon-btn hover:bg-error-container text-error" title="Entfernen">
                                            <DeleteIcon style={{ fontSize: 16 }} />
                                        </button>
                                    </>
                                )}
                            </div>
                        </div>
                    );
                })}
                {artifacts?.knowledge?.map((know, kIdx) => {
                    const gIdx = (proposedArtifacts.knowledge || []).findIndex(x => x.tempId === know.tempId);
                    const item = gIdx >= 0 ? proposedArtifacts.knowledge[gIdx] : know;
                    if (!item) return null;
                    const isProposed = gIdx >= 0;
                    return (
                        <div key={'know-' + kIdx} className="md3-card-outlined p-4 relative group hover:shadow-md transition-shadow animate-scale-in bg-surface-containerLow border-l-4 border-l-primary">
                            <div className="flex items-center justify-between gap-2 pr-16 mb-1">
                                <div className="flex items-center gap-1.5">
                                    <span className="text-[10px] font-bold text-primary uppercase tracking-wider">💡 Wissensdatenbank</span>
                                    {item.category && <span className="text-[10px] px-2 py-0.5 rounded-full bg-surface-containerHigh text-surface-onVariant">{item.category}</span>}
                                </div>
                                {!isProposed && (
                                    <span className="text-[10px] font-bold px-2 py-0.5 rounded bg-surface-containerHigh text-surface-onVariant flex-shrink-0">
                                        Gespeichert
                                    </span>
                                )}
                            </div>
                            <h4 className="font-bold text-body-large text-surface-on pr-12">{item.title}</h4>
                            <div className="mt-2 text-body-small text-surface-onVariant prose prose-sm max-h-[150px] overflow-y-auto whitespace-pre-wrap opacity-80 border border-outlineVariant/20 p-2 rounded bg-surface">
                                {item.text}
                            </div>
                            {item.sourceUrl && (
                                <a href={item.sourceUrl} target="_blank" rel="noopener noreferrer" className="text-xs text-primary hover:underline flex items-center gap-1 mt-2">
                                    🔗 Quelle: {item.sourceUrl.replace(/^https?:\/\//, '').split('/')[0]}
                                </a>
                            )}
                            {isProposed && (
                                <div className="absolute top-2 right-2 flex gap-1 opacity-60 group-hover:opacity-100 transition-opacity">
                                    <button onClick={() => handleOpenEdit('knowledge', gIdx)} className="md3-icon-btn hover:bg-surface-containerHigh text-surface-on" title="Bearbeiten">
                                        <EditIcon style={{ fontSize: 16 }} />
                                    </button>
                                    <button onClick={() => handleRemoveArtifact('knowledge', gIdx)} className="md3-icon-btn hover:bg-error-container text-error" title="Entfernen">
                                        <DeleteIcon style={{ fontSize: 16 }} />
                                    </button>
                                </div>
                            )}
                        </div>
                    );
                })}
                {actions?.map((a, aIdx) => {
                    const gIdx = proposedActions.findIndex(x => x.type === a.type && x.label === a.label);
                    const item = gIdx >= 0 ? proposedActions[gIdx] : a;
                    if (!item) return null;
                    const isProposed = gIdx >= 0;
                    return (
                        <div key={'a-' + aIdx} className="md3-card-outlined p-4 relative group hover:shadow-md transition-shadow animate-scale-in bg-surface-containerLow border-l-4 border-l-tertiary">
                            <div className="flex items-center justify-between gap-2 mb-2 pr-16">
                                <div className="flex items-center gap-2">
                                    <SettingsIcon style={{ fontSize: 16 }} className="text-tertiary" />
                                    <span className="text-[10px] font-black text-tertiary uppercase tracking-wider">
                                        {item.type === 'update_settings' ? 'Einstellung: ' + item.key
                                            : item.type === 'update_profile' ? '👤 Profil-Änderung'
                                                : item.type === 'update_team_member' ? '👥 Team-Mitglied'
                                                    : item.type === 'setup_email_routing' ? '✉️ E-Mail-Routing'
                                                        : item.type === 'update_attendance' ? '📋 Anwesenheit'
                                                            : item.type === 'add_group_member' ? '➕ Mitglied hinzufügen'
                                                                : item.type === 'remove_group_member' ? '➖ Mitglied entfernen'
                                                                    : item.type === 'approve_event_registration' ? '✓ Event-Anmeldung'
                                                                        : item.type === 'approve_user' ? '✓ Benutzer freischalten'
                                                                            : item.type === 'send_mailbox_email' ? '✉️ E-Mail senden'
                                                                                : item.type === 'update_contact_request' ? '📞 Kontaktanfrage'
                                                                                    : item.type}
                                    </span>
                                </div>
                                {!isProposed && (
                                    <span className="text-[10px] font-bold px-2 py-0.5 rounded bg-surface-containerHigh text-surface-onVariant flex-shrink-0">
                                        Ausgeführt / Verlauf
                                    </span>
                                )}
                            </div>
                            <h4 className="font-bold text-body-large text-surface-on pr-12">{item.label || 'Aktion'}</h4>
                            {isProposed && (
                                <div className="absolute top-2 right-2 flex gap-1 opacity-60 group-hover:opacity-100 transition-opacity">
                                    <button onClick={() => handleOpenActionEdit(gIdx)} className="md3-icon-btn hover:bg-surface-containerHigh text-surface-on" title="Bearbeiten">
                                        <EditIcon style={{ fontSize: 16 }} />
                                    </button>
                                    <button onClick={() => handleRemoveAction(gIdx)} className="md3-icon-btn hover:bg-error-container text-error" title="Entfernen">
                                        <DeleteIcon style={{ fontSize: 16 }} />
                                    </button>
                                </div>
                            )}
                        </div>
                    );
                })}
            </div>
        );
    };

    return (
        <div className="flex h-full animate-fade-in relative bg-surface">
            {/* Sidebar for Chat History */}
            <div className="w-64 border-r border-outlineVariant/30 bg-surface-containerLow flex flex-col hidden md:flex flex-shrink-0">
                <div className="p-4 border-b border-outlineVariant/30">
                    <button
                        onClick={handleNewChat}
                        className="w-full md3-btn-filled flex items-center justify-center gap-2"
                    >
                        <SparklesIcon style={{ fontSize: 18 }} /> Neuer Chat
                    </button>
                </div>
                <div className="flex-1 overflow-y-auto p-2 custom-scrollbar space-y-1">
                    {chatList.map(chat => (
                        <div
                            key={chat._id}
                            onClick={() => handleSelectChat(chat._id)}
                            className={`group flex items-center justify-between p-3 rounded-xl cursor-pointer transition-colors ${currentChatId === chat._id
                                    ? 'bg-secondary-container text-secondary-onContainer font-medium shadow-sm'
                                    : 'hover:bg-surface-on/5 text-surface-onVariant'
                                }`}
                        >
                            <div className="truncate flex-1 pr-2 text-body-medium">
                                {chat.title || 'Neuer Chat'}
                            </div>
                            <button
                                onClick={(e) => handleDeleteChat(chat._id, e)}
                                className={`md3-icon-btn text-error hover:bg-error-container/50 opacity-0 group-hover:opacity-100 transition-opacity flex-shrink-0 ${currentChatId === chat._id ? 'opacity-100' : ''}`}
                                title="Löschen"
                            >
                                <DeleteIcon style={{ fontSize: 16 }} />
                            </button>
                        </div>
                    ))}
                    {chatList.length === 0 && (
                        <div className="text-center text-body-small text-surface-onVariant/60 mt-4 px-2">
                            Noch keine Chats gespeichert.
                        </div>
                    )}
                </div>
            </div>

            {/* Main Chat Area */}
            <div className="flex-1 flex flex-col relative h-full min-w-0">
                <div className="container mx-auto p-4 max-w-4xl h-full flex flex-col gap-4">

                    {/* Error Banner */}
                    {error && (
                        <div className="p-4 rounded-xl bg-error-container text-error-on-container flex items-center gap-3 animate-fade-in mx-4 mt-4">
                            <AlertCircleIcon style={{ fontSize: 20 }} />
                            <span className="text-body-medium flex-1">{error}</span>
                            <button onClick={() => setError('')} className="text-error"><CloseIcon style={{ fontSize: 18 }} /></button>
                        </div>
                    )}

                    {/* Success Banner */}
                    {saveSuccess && (
                        <div className="p-4 rounded-xl bg-primary-container text-primary-onContainer flex items-center justify-between shadow-sm animate-scale-in mx-4 mt-4">
                            <div className="flex items-center gap-3">
                                <CheckCircleIcon style={{ fontSize: 24 }} />
                                <div>
                                    <h3 className="text-title-medium font-bold">Erfolgreich gespeichert!</h3>
                                    <p className="text-body-small">Die Elemente wurden in der Datenbank angelegt.</p>
                                </div>
                            </div>
                            <div className="flex gap-2">
                                {(saveResult?.events?.length > 0 || saveResult?.blogPosts?.length > 0 || saveResult?.tasks?.length > 0) && (
                                    <button onClick={() => navigate('/events')} className="md3-btn-tonal text-xs py-1.5 px-3">
                                        Zu den Events
                                    </button>
                                )}
                                <button onClick={() => setSaveSuccess(false)} className="md3-icon-btn hover:bg-primary-on/10"><CloseIcon style={{ fontSize: 18 }} /></button>
                            </div>
                        </div>
                    )}

                    {/* Conversational Chat Panel */}
                    <div className="flex-1 flex flex-col bg-surface-containerLow rounded-[2rem] border border-outlineVariant/30 shadow-elevation-2 mt-4 min-h-0">
                        {/* Chat Header */}
                        <div className="px-6 pt-6 pb-4 border-b border-outlineVariant/30 bg-surface-container flex items-center justify-between rounded-t-[2rem]">
                            <div className="flex items-center gap-4">
                                <div className="w-12 h-12 relative flex-shrink-0">
                                    <img
                                        src={getAssetUrl ? getAssetUrl('Bob_start.png') : (API_URL.replace('/api', '') + '/assets/Bob_start.png')}
                                        alt="Bob"
                                        className="absolute bottom-0 left-1/2 -translate-x-1/2 w-[220%] max-w-none object-contain origin-bottom"
                                        onError={(e) => { e.target.style.display = 'none'; e.target.nextSibling.style.display = 'block'; }}
                                    />
                                    <SparklesIcon style={{ fontSize: 22, color: 'currentColor', display: 'none' }} className="fallback-icon relative z-10 text-primary" />
                                </div>
                                <div>
                                    <h2 className="text-title-medium font-bold text-surface-on">Bob</h2>
                                    <p className="text-body-small text-surface-onVariant">Dein schlauer KI-Assistent</p>
                                </div>
                            </div>

                            {currentChatId && (
                                <div className="flex items-center gap-2 relative" ref={addMenuRef}>
                                    {/* List of participants */}
                                    <div className="flex -space-x-2 overflow-hidden">
                                        {currentParticipants.map(pId => {
                                            const name = getMemberName(pId);
                                            const initials = name.split(' ').map(n => n[0]).join('').substring(0, 2).toUpperCase();
                                            const isSelf = pId === user.id || pId === user._id;
                                            return (
                                                <div
                                                    key={pId}
                                                    title={name + (isSelf ? ' (Du)' : '')}
                                                    className="inline-flex items-center justify-center w-8 h-8 rounded-full bg-secondary text-secondary-on border-2 border-surface text-[10px] font-bold select-none cursor-help relative group"
                                                >
                                                    {initials || '👤'}
                                                </div>
                                            );
                                        })}
                                    </div>

                                    {/* Dropdown triggers */}
                                    <div className="relative">
                                        <button
                                            onClick={() => setShowAddMenu(!showAddMenu)}
                                            className="md3-icon-btn hover:bg-surface-containerHigh text-primary"
                                            title="Leute hinzufügen"
                                            type="button"
                                        >
                                            <AddUserIcon style={{ fontSize: 20 }} />
                                        </button>

                                        {showAddMenu && (
                                            <div className="absolute right-0 mt-2 w-56 bg-surface-containerHigh border border-outlineVariant/50 rounded-xl shadow-lg z-30 py-2 animate-scale-in">
                                                <div className="px-3 py-1 text-xs font-bold text-surface-onVariant/60 uppercase tracking-wider border-b border-outlineVariant/20 mb-1">
                                                    Person hinzufügen
                                                </div>
                                                <div className="max-h-48 overflow-y-auto custom-scrollbar">
                                                    {teamMembers
                                                        .filter(m => m._id !== user.id && m._id !== user._id && !currentParticipants.includes(m._id))
                                                        .map(member => {
                                                            const name = member.userData?.displayName || `${member.userData?.firstName || ''} ${member.userData?.lastName || ''}`.trim() || member.email;
                                                            return (
                                                                <button
                                                                    key={member._id}
                                                                    type="button"
                                                                    onClick={() => {
                                                                        handleAddParticipant(member._id);
                                                                        setShowAddMenu(false);
                                                                    }}
                                                                    className="w-full text-left px-4 py-2 hover:bg-surface-on/5 text-body-medium text-surface-on transition-colors"
                                                                >
                                                                    {name}
                                                                </button>
                                                            );
                                                        })
                                                    }
                                                    {teamMembers.filter(m => !currentParticipants.includes(m._id)).length === 0 && (
                                                        <div className="px-4 py-2 text-xs text-surface-onVariant/50">
                                                            Keine weiteren Mitglieder
                                                        </div>
                                                    )}
                                                </div>
                                                {currentParticipants.length > 1 && (
                                                    <>
                                                        <div className="border-t border-outlineVariant/20 my-1"></div>
                                                        <div className="px-3 py-1 text-xs font-bold text-surface-onVariant/60 uppercase tracking-wider mb-1">
                                                            Teilnehmer entfernen
                                                        </div>
                                                        <div className="max-h-36 overflow-y-auto custom-scrollbar">
                                                            {currentParticipants
                                                                .filter(pId => pId !== user.id && pId !== user._id)
                                                                .map(pId => {
                                                                    const name = getMemberName(pId);
                                                                    return (
                                                                        <div
                                                                            key={pId}
                                                                            className="flex items-center justify-between px-4 py-1.5 hover:bg-error-container/20 group"
                                                                        >
                                                                            <span className="text-body-medium text-surface-on truncate mr-2">{name}</span>
                                                                            <button
                                                                                type="button"
                                                                                onClick={() => handleRemoveParticipant(pId)}
                                                                                className="text-error hover:bg-error-container rounded p-0.5"
                                                                                title="Entfernen"
                                                                            >
                                                                                <DeleteIcon style={{ fontSize: 14 }} />
                                                                            </button>
                                                                        </div>
                                                                    );
                                                                })
                                                            }
                                                        </div>
                                                    </>
                                                )}
                                            </div>
                                        )}
                                    </div>
                                </div>
                            )}
                        </div>

                        {/* Messages View */}
                        <div ref={chatContainerRef} className="flex-grow overflow-y-auto p-6 space-y-6 custom-scrollbar">
                            {messages.map((msg, idx) => {
                                const isUser = msg.role === 'user';
                                const isAssistant = msg.role === 'assistant';
                                const assistantObj = isAssistant && typeof msg.content === 'object' ? msg.content : null;
                                const msgText = (assistantObj ? (assistantObj.text || assistantObj.message || assistantObj.response || assistantObj.reply || '') : (typeof msg.content === 'string' ? msg.content : '')) || (isAssistant ? 'Antwort von Bob erhalten.' : '');
                                const grounding = assistantObj?.grounding || msg.grounding;
                                const uiElements = assistantObj?.ui || msg.ui;

                                return (
                                    <div key={idx} className={`flex ${isUser ? 'justify-end items-end' : 'justify-start items-start'} animate-scale-in flex-col gap-1 w-full`}>
                                        {isUser && msg.senderName && (
                                            <span className="text-[10px] font-bold text-surface-onVariant/60 self-end mr-2 select-none">
                                                {msg.senderName}
                                            </span>
                                        )}
                                        {isAssistant && (
                                            <div className="flex items-center gap-1.5 ml-2 mb-0.5 select-none text-[11px] font-bold text-surface-onVariant/70">
                                                <div className="w-5 h-5 rounded-full overflow-hidden flex items-center justify-center bg-primary/10">
                                                    <SparklesIcon style={{ fontSize: 13 }} className="text-primary" />
                                                </div>
                                                <span>Bob</span>
                                            </div>
                                        )}
                                        <div className={`max-w-[85%] p-4 rounded-[1.5rem] shadow-sm ${isUser
                                                ? 'bg-primary text-primary-on rounded-tr-none self-end whitespace-pre-wrap'
                                                : 'bg-surface-containerHigh text-surface-on rounded-tl-none border border-outlineVariant/20 self-start'
                                            }`}>
                                            {isUser ? (
                                                <p className="text-body-medium">{msgText}</p>
                                            ) : (
                                                <BobMarkdown
                                                    content={msgText}
                                                    onLinkClick={(url) => {
                                                        if (url.startsWith('http')) window.open(url, '_blank', 'noopener,noreferrer');
                                                        else navigate(url);
                                                    }}
                                                />
                                            )}
                                            {msg.imageUrl && (
                                                <div className="mt-2 max-w-[240px] overflow-hidden rounded-lg border border-outlineVariant/25 bg-black/5">
                                                    <img src={msg.imageUrl} alt={msg.imageKey || 'Attached image'} className="w-full h-auto max-h-[160px] object-cover" />
                                                </div>
                                            )}
                                        </div>

                                        {/* Google Search Grounding Sources */}
                                        {isAssistant && grounding && (
                                            <div className="self-start w-full">
                                                <BobGroundingSources grounding={grounding} />
                                            </div>
                                        )}

                                        {/* Rich UI Elements (Callouts, Quick Replies, Event Cards, Blog Cards, etc.) */}
                                        {isAssistant && uiElements && uiElements.length > 0 && (
                                            <div className="self-start w-full">
                                                <BobRichElements
                                                    ui={uiElements}
                                                    onQuickReply={(replyText) => handleQuickReply(replyText)}
                                                    onAction={handleRichAction}
                                                />
                                            </div>
                                        )}

                                        {/* Render Inline Artifacts */}
                                        {isAssistant && assistantObj && (assistantObj.artifacts || assistantObj.actions) && (
                                            renderInlineArtifacts(assistantObj.artifacts, assistantObj.actions)
                                        )}
                                    </div>
                                );
                            })}

                            {/* Render Typing Indicators */}
                            {Object.entries(typingUsers)
                                .filter(([uName, isTyping]) => isTyping && uName !== (user.userData?.firstName || 'Pfadfinder'))
                                .map(([uName]) => (
                                    <div key={uName} className="flex justify-start items-center gap-2 text-body-small text-surface-onVariant/70 animate-scale-in">
                                        <div className="bg-surface-containerHigh p-3 rounded-[1.5rem] rounded-tl-none border border-outlineVariant/20 flex gap-1 items-center">
                                            <span className="w-1.5 h-1.5 rounded-full bg-primary animate-bounce" style={{ animationDelay: '0ms' }} />
                                            <span className="w-1.5 h-1.5 rounded-full bg-primary animate-bounce" style={{ animationDelay: '150ms' }} />
                                            <span className="w-1.5 h-1.5 rounded-full bg-primary animate-bounce" style={{ animationDelay: '300ms' }} />
                                        </div>
                                        <span className="text-xs font-semibold">{uName} schreibt...</span>
                                    </div>
                                ))
                            }

                            {loading && (
                                <div className="flex justify-start">
                                    <div className="bg-surface-containerHigh p-4 rounded-[1.5rem] rounded-tl-none border border-outlineVariant/20 flex gap-1 items-center">
                                        <span className="w-2 h-2 rounded-full bg-primary animate-bounce" style={{ animationDelay: '0ms' }} />
                                        <span className="w-2 h-2 rounded-full bg-primary animate-bounce" style={{ animationDelay: '150ms' }} />
                                        <span className="w-2 h-2 rounded-full bg-primary animate-bounce" style={{ animationDelay: '300ms' }} />
                                    </div>
                                </div>
                            )}
                        </div>

                        {/* Sticky Save Button Container */}
                        {(artifactCounts.events > 0 || artifactCounts.blogs > 0 || artifactCounts.tasks > 0 || artifactCounts.texts > 0 || artifactCounts.actions > 0) && (
                            <div className="p-4 border-t border-outlineVariant/30 bg-surface-container shadow-[0_-8px_24px_rgba(0,0,0,0.1)] relative z-10 flex flex-col gap-2">
                                <div className="text-body-small text-surface-onVariant text-center flex items-center justify-center gap-2">
                                    <SparklesIcon style={{ fontSize: 16 }} />
                                    Du hast ungespeicherte Entwürfe: {artifactCounts.events} Events, {artifactCounts.blogs} Blogs, {artifactCounts.tasks} Tasks, {artifactCounts.texts} Texte, {artifactCounts.actions} Aktionen
                                </div>
                                <button
                                    onClick={handleSaveAll}
                                    disabled={saving}
                                    className="w-full h-12 rounded-2xl flex items-center justify-center gap-2 bg-primary text-primary-on font-bold shadow-sm hover:scale-[1.02] active:scale-[0.98] transition-all disabled:opacity-50"
                                >
                                    {saving ? (
                                        <><RefreshIcon className="animate-spin" style={{ fontSize: 18 }} /> {saveProgress}</>
                                    ) : (
                                        <><CheckCircleIcon style={{ fontSize: 18 }} /> Alle Änderungen speichern</>
                                    )}
                                </button>
                            </div>
                        )}

                        {/* Image upload preview banner */}
                        {uploadedImage && (
                            <div className="flex items-center gap-3 px-6 py-3 bg-surface-container border-t border-outlineVariant/30 animate-scale-in">
                                <div className="relative w-12 h-12 rounded-lg overflow-hidden border border-outlineVariant flex-shrink-0">
                                    <img src={uploadedImage.url} alt="Preview" className="w-full h-full object-cover" />
                                </div>
                                <div className="flex-grow min-w-0">
                                    <div className="text-body-small font-bold text-surface-on truncate">{uploadedImage.key}</div>
                                    <div className="text-[10px] text-primary">Bild bereit zum Anhängen</div>
                                </div>
                                <button
                                    type="button"
                                    onClick={() => setUploadedImage(null)}
                                    className="w-8 h-8 rounded-full flex items-center justify-center hover:bg-error-container text-error transition-colors"
                                    title="Entfernen"
                                >
                                    <CloseIcon style={{ fontSize: 16 }} />
                                </button>
                            </div>
                        )}
                        {uploadingImage && (
                            <div className="flex items-center gap-3 px-6 py-3 bg-surface-container border-t border-outlineVariant/30 animate-pulse">
                                <RefreshIcon className="animate-spin text-primary flex-shrink-0" style={{ fontSize: 18 }} />
                                <span className="text-body-small text-surface-onVariant">Bild wird hochgeladen...</span>
                            </div>
                        )}

                        {/* Input Bar */}
                        <form onSubmit={handleSend} className="p-4 border-t border-outlineVariant/30 bg-surface-container flex gap-3 items-center rounded-b-[2rem]">
                            <input
                                type="file"
                                ref={fileInputRef}
                                onChange={handleImageUpload}
                                accept="image/*"
                                style={{ display: 'none' }}
                            />
                            <button
                                type="button"
                                onClick={() => fileInputRef.current?.click()}
                                disabled={loading || uploadingImage}
                                className="w-12 h-12 rounded-full flex items-center justify-center bg-surface-containerHigh border border-outlineVariant text-surface-on shadow-sm hover:scale-105 active:scale-95 disabled:opacity-50 transition-all flex-shrink-0"
                                title="Bild hochladen"
                            >
                                <UploadIcon style={{ fontSize: 20 }} />
                            </button>
                            <input
                                type="text"
                                value={input}
                                onChange={handleInputChange}
                                onPaste={handlePaste}
                                placeholder="Was soll ich tun? (z.B. Text eingeben oder Flyer einfügen)..."
                                disabled={loading}
                                className="flex-grow h-12 px-6 rounded-full bg-surface-containerHigh border border-outlineVariant text-surface-on placeholder-surface-onVariant/60 focus:outline-none focus:border-primary transition-all disabled:opacity-50"
                            />
                            <button
                                type="submit"
                                disabled={(!input.trim() && !uploadedImage) || loading}
                                className="w-12 h-12 rounded-full flex items-center justify-center bg-primary text-primary-on shadow-sm hover:scale-105 active:scale-95 disabled:opacity-50 transition-all flex-shrink-0"
                            >
                                <SendIcon style={{ fontSize: 20 }} />
                            </button>
                        </form>
                    </div>
                </div>
            </div>

            {/* EDIT MODAL */}
            {editModal.data && (
                <div className="fixed inset-0 bg-scrim/40 backdrop-blur-sm flex items-center justify-center z-50 p-4 animate-fade-in">
                    <div className="md3-card-elevated max-w-xl w-full p-6 animate-scale-in max-h-[90vh] overflow-y-auto">
                        <h3 className="text-title-large font-bold text-surface-on mb-4">Entwurf bearbeiten</h3>

                        <form onSubmit={handleSaveEdit} className="space-y-4">
                            {/* EVENT EDIT FORM */}
                            {editModal.type === 'events' && (
                                <>
                                    <div>
                                        <label className="block text-label-medium text-surface-onVariant mb-1">Titel</label>
                                        <input
                                            type="text"
                                            value={editModal.data.title || ''}
                                            onChange={e => setEditModal(prev => ({ ...prev, data: { ...prev.data, title: e.target.value } }))}
                                            className="md3-textfield-outlined"
                                            required
                                        />
                                    </div>
                                    <div className="grid grid-cols-2 gap-4">
                                        <div>
                                            <label className="block text-label-medium text-surface-onVariant mb-1">Datum</label>
                                            <input
                                                type="date"
                                                value={editModal.data.date || ''}
                                                onChange={e => setEditModal(prev => ({ ...prev, data: { ...prev.data, date: e.target.value } }))}
                                                className="md3-textfield-outlined"
                                            />
                                        </div>
                                        <div>
                                            <label className="block text-label-medium text-surface-onVariant mb-1">Ort</label>
                                            <input
                                                type="text"
                                                value={editModal.data.location || ''}
                                                onChange={e => setEditModal(prev => ({ ...prev, data: { ...prev.data, location: e.target.value } }))}
                                                className="md3-textfield-outlined"
                                            />
                                        </div>
                                    </div>
                                    <div className="grid grid-cols-2 gap-4">
                                        <div>
                                            <label className="block text-label-medium text-surface-onVariant mb-1">Startzeit</label>
                                            <input
                                                type="time"
                                                value={editModal.data.startTime || ''}
                                                onChange={e => setEditModal(prev => ({ ...prev, data: { ...prev.data, startTime: e.target.value } }))}
                                                className="md3-textfield-outlined"
                                            />
                                        </div>
                                        <div>
                                            <label className="block text-label-medium text-surface-onVariant mb-1">Endzeit</label>
                                            <input
                                                type="time"
                                                value={editModal.data.endTime || ''}
                                                onChange={e => setEditModal(prev => ({ ...prev, data: { ...prev.data, endTime: e.target.value } }))}
                                                className="md3-textfield-outlined"
                                            />
                                        </div>
                                    </div>
                                    <div>
                                        <label className="block text-label-medium text-surface-onVariant mb-1">Kategorie</label>
                                        <select
                                            value={editModal.data.category || 'Sonstiges'}
                                            onChange={e => setEditModal(prev => ({ ...prev, data: { ...prev.data, category: e.target.value } }))}
                                            className="md3-textfield-outlined h-12"
                                        >
                                            <option value="Gruppenstunde">Gruppenstunde</option>
                                            <option value="Lager">Lager</option>
                                            <option value="Aktion">Aktion</option>
                                            <option value="Sonstiges">Sonstiges</option>
                                        </select>
                                    </div>
                                    <div>
                                        <label className="block text-label-medium text-surface-onVariant mb-1">Beschreibung</label>
                                        <textarea
                                            value={editModal.data.description || ''}
                                            onChange={e => setEditModal(prev => ({ ...prev, data: { ...prev.data, description: e.target.value } }))}
                                            className="md3-textfield-outlined min-h-[80px] py-2"
                                        />
                                    </div>
                                    <div className="grid grid-cols-2 gap-x-4 gap-y-2 mt-3 bg-surface-container/30 p-3 rounded-xl border border-outlineVariant/10">
                                        <label className="flex items-center gap-2 cursor-pointer">
                                            <input
                                                type="checkbox"
                                                checked={editModal.data.isRegistrationEnabled || false}
                                                onChange={e => setEditModal(prev => ({ ...prev, data: { ...prev.data, isRegistrationEnabled: e.target.checked } }))}
                                                className="accent-primary w-4 h-4"
                                            />
                                            <span className="text-body-medium font-bold text-surface-on">Anmeldung aktivieren</span>
                                        </label>
                                        <label className="flex items-center gap-2 cursor-pointer">
                                            <input
                                                type="checkbox"
                                                checked={editModal.data.requiresApproval || false}
                                                onChange={e => setEditModal(prev => ({ ...prev, data: { ...prev.data, requiresApproval: e.target.checked } }))}
                                                className="accent-primary w-4 h-4"
                                            />
                                            <span className="text-body-medium text-surface-on">Bestätigung erforderlich</span>
                                        </label>
                                        <label className="flex items-center gap-2 cursor-pointer">
                                            <input
                                                type="checkbox"
                                                checked={editModal.data.requiresConsent || false}
                                                onChange={e => setEditModal(prev => ({ ...prev, data: { ...prev.data, requiresConsent: e.target.checked } }))}
                                                className="accent-primary w-4 h-4"
                                            />
                                            <span className="text-body-medium text-surface-on">Einverständnis nötig</span>
                                        </label>
                                        <label className="flex items-center gap-2 cursor-pointer">
                                            <input
                                                type="checkbox"
                                                checked={editModal.data.askHealthQuestions || false}
                                                onChange={e => setEditModal(prev => ({ ...prev, data: { ...prev.data, askHealthQuestions: e.target.checked } }))}
                                                className="accent-primary w-4 h-4"
                                            />
                                            <span className="text-body-medium text-surface-on">Gesundheitsfragen stellen</span>
                                        </label>
                                        <label className="flex items-center gap-2 cursor-pointer">
                                            <input
                                                type="checkbox"
                                                checked={editModal.data.hasDetailPage !== false}
                                                onChange={e => setEditModal(prev => ({ ...prev, data: { ...prev.data, hasDetailPage: e.target.checked } }))}
                                                className="accent-primary w-4 h-4"
                                            />
                                            <span className="text-body-medium text-surface-on">Eigene Detailseite</span>
                                        </label>
                                        <label className="flex items-center gap-2 cursor-pointer">
                                            <input
                                                type="checkbox"
                                                checked={editModal.data.includeConsentSection || false}
                                                onChange={e => setEditModal(prev => ({ ...prev, data: { ...prev.data, includeConsentSection: e.target.checked } }))}
                                                className="accent-primary w-4 h-4"
                                            />
                                            <span className="text-body-medium text-surface-on">Einverständnis-Sekt.</span>
                                        </label>
                                    </div>
                                    {editModal.data.requiresConsent && (
                                        <div className="mt-3 animate-fade-in">
                                            <label className="block text-label-medium text-surface-onVariant mb-1">Einverständnis-Formular URL</label>
                                            <input
                                                type="text"
                                                value={editModal.data.consentFormUrl || ''}
                                                onChange={e => setEditModal(prev => ({ ...prev, data: { ...prev.data, consentFormUrl: e.target.value } }))}
                                                className="md3-textfield-outlined"
                                                placeholder="https://..."
                                            />
                                        </div>
                                    )}
                                    <div className="mt-3">
                                        <label className="block text-label-medium text-surface-onVariant mb-1">Detailseite-Sektionen (JSON)</label>
                                        <textarea
                                            value={typeof editModal.data.sections === 'object' ? JSON.stringify(editModal.data.sections, null, 2) : editModal.data.sections || '[]'}
                                            onChange={e => {
                                                const val = e.target.value;
                                                setEditModal(prev => {
                                                    let parsed = val;
                                                    try {
                                                        parsed = JSON.parse(val);
                                                    } catch (_) { }
                                                    return { ...prev, data: { ...prev.data, sections: parsed } };
                                                });
                                            }}
                                            className="md3-textfield-outlined font-mono text-xs min-h-[100px] py-2"
                                            placeholder="[ { &quot;type&quot;: &quot;text&quot;, &quot;title&quot;: &quot;...&quot;, &quot;content&quot;: &quot;...&quot; } ]"
                                        />
                                    </div>
                                    <div className="mt-3">
                                        <label className="block text-label-medium text-surface-onVariant mb-1">Eigene Anmelde-Felder (JSON)</label>
                                        <textarea
                                            value={typeof editModal.data.customFields === 'object' ? JSON.stringify(editModal.data.customFields, null, 2) : editModal.data.customFields || '[]'}
                                            onChange={e => {
                                                const val = e.target.value;
                                                setEditModal(prev => {
                                                    let parsed = val;
                                                    try {
                                                        parsed = JSON.parse(val);
                                                    } catch (_) { }
                                                    return { ...prev, data: { ...prev.data, customFields: parsed } };
                                                });
                                            }}
                                            className="md3-textfield-outlined font-mono text-xs min-h-[100px] py-2"
                                            placeholder="[ { &quot;name&quot;: &quot;T-Shirt Größe&quot;, &quot;type&quot;: &quot;select&quot;, &quot;required&quot;: true, &quot;options&quot;: [&quot;S&quot;,&quot;M&quot;] } ]"
                                        />
                                    </div>
                                </>
                            )}

                            {/* BLOG EDIT FORM */}
                            {editModal.type === 'blogPosts' && (
                                <>
                                    <div>
                                        <label className="block text-label-medium text-surface-onVariant mb-1">Titel</label>
                                        <input
                                            type="text"
                                            value={editModal.data.title || ''}
                                            onChange={e => setEditModal(prev => ({ ...prev, data: { ...prev.data, title: e.target.value } }))}
                                            className="md3-textfield-outlined"
                                            required
                                        />
                                    </div>
                                    <div>
                                        <label className="block text-label-medium text-surface-onVariant mb-1">Inhalt (HTML)</label>
                                        <textarea
                                            value={editModal.data.content || ''}
                                            onChange={e => setEditModal(prev => ({ ...prev, data: { ...prev.data, content: e.target.value } }))}
                                            className="md3-textfield-outlined font-mono min-h-[150px] py-2 text-body-small"
                                            required
                                        />
                                    </div>
                                    <div>
                                        <label className="block text-label-medium text-surface-onVariant mb-1">Verknüpftes Event</label>
                                        <select
                                            value={editModal.data.linkedEvent || ''}
                                            onChange={e => setEditModal(prev => ({ ...prev, data: { ...prev.data, linkedEvent: e.target.value } }))}
                                            className="md3-textfield-outlined h-12"
                                        >
                                            <option value="">-- Keines --</option>
                                            {/* Proposed events first */}
                                            {proposedArtifacts.events.map(pe => (
                                                <option key={pe.tempId} value={pe.tempId}>[Vorgeschlagen] {pe.title}</option>
                                            ))}
                                            {/* Existing events */}
                                            {existingEvents.map(ee => (
                                                <option key={ee._id} value={ee._id}>{ee.title}</option>
                                            ))}
                                        </select>
                                    </div>
                                </>
                            )}

                            {/* TASK EDIT FORM */}
                            {editModal.type === 'tasks' && (
                                <>
                                    <div>
                                        <label className="block text-label-medium text-surface-onVariant mb-1">Titel</label>
                                        <input
                                            type="text"
                                            value={editModal.data.title || ''}
                                            onChange={e => setEditModal(prev => ({ ...prev, data: { ...prev.data, title: e.target.value } }))}
                                            className="md3-textfield-outlined"
                                            required
                                        />
                                    </div>
                                    <div>
                                        <label className="block text-label-medium text-surface-onVariant mb-1">Beschreibung</label>
                                        <textarea
                                            value={editModal.data.description || ''}
                                            onChange={e => setEditModal(prev => ({ ...prev, data: { ...prev.data, description: e.target.value } }))}
                                            className="md3-textfield-outlined min-h-[80px] py-2"
                                        />
                                    </div>
                                    <div className="grid grid-cols-2 gap-4">
                                        <div>
                                            <label className="block text-label-medium text-surface-onVariant mb-1">Priorität</label>
                                            <select
                                                value={editModal.data.priority || 'medium'}
                                                onChange={e => setEditModal(prev => ({ ...prev, data: { ...prev.data, priority: e.target.value } }))}
                                                className="md3-textfield-outlined h-12"
                                            >
                                                <option value="low">Niedrig</option>
                                                <option value="medium">Mittel</option>
                                                <option value="high">Hoch</option>
                                                <option value="urgent">Dringend</option>
                                            </select>
                                        </div>
                                        <div>
                                            <label className="block text-label-medium text-surface-onVariant mb-1">Frist (Deadline)</label>
                                            <input
                                                type="date"
                                                value={editModal.data.deadline || ''}
                                                onChange={e => setEditModal(prev => ({ ...prev, data: { ...prev.data, deadline: e.target.value } }))}
                                                className="md3-textfield-outlined"
                                            />
                                        </div>
                                    </div>
                                    <div>
                                        <label className="block text-label-medium text-surface-onVariant mb-1">Zugewiesenes Event</label>
                                        <select
                                            value={editModal.data.event || ''}
                                            onChange={e => setEditModal(prev => ({ ...prev, data: { ...prev.data, event: e.target.value } }))}
                                            className="md3-textfield-outlined h-12"
                                        >
                                            <option value="">-- Keines --</option>
                                            {/* Proposed events first */}
                                            {proposedArtifacts.events.map(pe => (
                                                <option key={pe.tempId} value={pe.tempId}>[Vorgeschlagen] {pe.title}</option>
                                            ))}
                                            {/* Existing events */}
                                            {existingEvents.map(ee => (
                                                <option key={ee._id} value={ee._id}>{ee.title}</option>
                                            ))}
                                        </select>
                                    </div>
                                    <div>
                                        <label className="block text-label-medium text-surface-onVariant mb-2">Zugewiesen an</label>
                                        <div className="flex flex-wrap gap-2">
                                            {teamMembers.map(u => {
                                                const isAssigned = editModal.data.assignees?.includes(u._id);
                                                return (
                                                    <button
                                                        key={u._id}
                                                        type="button"
                                                        onClick={() => {
                                                            const assignees = editModal.data.assignees || [];
                                                            const newAssignees = isAssigned
                                                                ? assignees.filter(id => id !== u._id)
                                                                : [...assignees, u._id];
                                                            setEditModal(prev => ({ ...prev, data: { ...prev.data, assignees: newAssignees } }));
                                                        }}
                                                        className={`px-3 py-1 rounded-full text-xs font-bold transition-all ${isAssigned
                                                                ? 'bg-primary text-primary-on'
                                                                : 'bg-surface-containerHigh text-surface-onVariant hover:bg-outlineVariant/30'
                                                            }`}
                                                    >
                                                        {u.userData?.scoutName || u.userData?.firstName || 'Nutzer'}
                                                    </button>
                                                );
                                            })}
                                        </div>
                                    </div>
                                </>
                            )}

                            {/* TEXTS EDIT FORM */}
                            {editModal.type === 'texts' && (
                                <>
                                    <div>
                                        <label className="block text-label-medium text-surface-onVariant mb-1">Titel / Betreff</label>
                                        <input
                                            type="text"
                                            value={editModal.data.title || ''}
                                            onChange={e => setEditModal(prev => ({ ...prev, data: { ...prev.data, title: e.target.value } }))}
                                            className="md3-textfield-outlined"
                                            required
                                        />
                                    </div>
                                    <div>
                                        <label className="block text-label-medium text-surface-onVariant mb-1">Inhalt</label>
                                        <textarea
                                            value={editModal.data.content || ''}
                                            onChange={e => setEditModal(prev => ({ ...prev, data: { ...prev.data, content: e.target.value } }))}
                                            className="md3-textfield-outlined min-h-[200px] py-2"
                                            required
                                        />
                                    </div>
                                </>
                            )}

                            {/* KNOWLEDGE EDIT FORM */}
                            {editModal.type === 'knowledge' && (
                                <>
                                    <div>
                                        <label className="block text-label-medium text-surface-onVariant mb-1">Titel</label>
                                        <input
                                            type="text"
                                            value={editModal.data.title || ''}
                                            onChange={e => setEditModal(prev => ({ ...prev, data: { ...prev.data, title: e.target.value } }))}
                                            className="md3-textfield-outlined"
                                            required
                                        />
                                    </div>
                                    <div>
                                        <label className="block text-label-medium text-surface-onVariant mb-1">Kategorie</label>
                                        <input
                                            type="text"
                                            value={editModal.data.category || ''}
                                            onChange={e => setEditModal(prev => ({ ...prev, data: { ...prev.data, category: e.target.value } }))}
                                            className="md3-textfield-outlined"
                                            placeholder="z.B. Pfadfinderwissen, Organisation, Ausrüstung"
                                        />
                                    </div>
                                    <div>
                                        <label className="block text-label-medium text-surface-onVariant mb-1">Wissensinhalt / Zusammenfassung</label>
                                        <textarea
                                            value={editModal.data.text || editModal.data.content || ''}
                                            onChange={e => setEditModal(prev => ({ ...prev, data: { ...prev.data, text: e.target.value, content: e.target.value } }))}
                                            className="md3-textfield-outlined min-h-[160px] py-2"
                                            required
                                        />
                                    </div>
                                    <div>
                                        <label className="block text-label-medium text-surface-onVariant mb-1">Quelle / Link (optional)</label>
                                        <input
                                            type="url"
                                            value={editModal.data.sourceUrl || ''}
                                            onChange={e => setEditModal(prev => ({ ...prev, data: { ...prev.data, sourceUrl: e.target.value } }))}
                                            className="md3-textfield-outlined"
                                            placeholder="https://..."
                                        />
                                    </div>
                                </>
                            )}

                            {/* ACTION EDIT FORM */}
                            {editModal.type === 'action' && (
                                <>
                                    <div>
                                        <label className="block text-label-medium text-surface-onVariant mb-1">Typ</label>
                                        <input
                                            type="text"
                                            value={editModal.data.type || ''}
                                            disabled
                                            className="md3-textfield-outlined opacity-60 bg-surface-containerHigh cursor-not-allowed"
                                        />
                                    </div>
                                    <div>
                                        <label className="block text-label-medium text-surface-onVariant mb-1">Beschreibung</label>
                                        <input
                                            type="text"
                                            value={editModal.data.label || ''}
                                            onChange={e => setEditModal(prev => ({ ...prev, data: { ...prev.data, label: e.target.value } }))}
                                            className="md3-textfield-outlined"
                                        />
                                    </div>

                                    {(editModal.data.type === 'update_profile' || editModal.data.type === 'update_team_member') && (
                                        <div>
                                            <label className="block text-label-medium text-surface-onVariant mb-1">Profil-Daten (JSON)</label>
                                            <textarea
                                                value={typeof editModal.data.data === 'object' ? JSON.stringify(editModal.data.data, null, 2) : editModal.data.data || '{}'}
                                                onChange={e => {
                                                    const val = e.target.value;
                                                    setEditModal(prev => {
                                                        let parsed = val;
                                                        try {
                                                            parsed = JSON.parse(val);
                                                        } catch (_) { }
                                                        return { ...prev, data: { ...prev.data, data: parsed } };
                                                    });
                                                }}
                                                className="md3-textfield-outlined font-mono text-xs min-h-[200px] py-2 custom-scrollbar"
                                                placeholder="{ &quot;scoutName&quot;: &quot;...&quot; }"
                                            />
                                        </div>
                                    )}

                                    {(editModal.data.key === 'llms' || editModal.data.key === 'robots') && (
                                        <div>
                                            <label className="block text-label-medium text-surface-onVariant mb-1">
                                                Inhalt ({editModal.data.key === 'robots' ? 'robots.txt' : 'llms.txt'})
                                            </label>
                                            <textarea
                                                value={editModal.data.data?.content || ''}
                                                onChange={e => setEditModal(prev => ({ ...prev, data: { ...prev.data, data: { ...prev.data.data, content: e.target.value } } }))}
                                                className="md3-textfield-outlined font-mono min-h-[300px] py-2 text-body-small custom-scrollbar"
                                            />
                                        </div>
                                    )}

                                    {editModal.data.key === 'homepage' && (
                                        <div>
                                            <p className="text-body-medium text-surface-onVariant">
                                                Die Homepage-Einstellungen liegen als rohes JSON vor. Bearbeitung aktuell nur im Code möglich.
                                            </p>
                                        </div>
                                    )}
                                </>
                            )}

                            <div className="flex gap-3 pt-4 border-t border-outlineVariant/30">
                                <button type="submit" className="md3-btn-filled flex-grow py-3">
                                    Übernehmen
                                </button>
                                <button
                                    type="button"
                                    onClick={() => setEditModal({ type: '', index: null, data: null })}
                                    className="md3-btn-text flex-grow py-3"
                                >
                                    Abbrechen
                                </button>
                            </div>
                        </form>
                    </div>
                </div>
            )}
        </div>
    );
};

export default AiAgent;

